<template>
  <div class="schedule" v-if="report.user_data">
    <ReportDraftWatermark v-if="report.is_draft" />
    <h2 class="formTitle">Schedule A: Cash Receipts and Expenditure Detail</h2>
    <v-divider></v-divider>
    <div>
      <h3 class="line-item-title">
        1. Contributions and other cash receipts reported on the C3:
      </h3>
      <div class="newspaper-cols-container">
        <div class="newspaperColumnLayout" v-for="(column, index) in columns" :key="index">
          <div v-if="arrayHasItems(columns)">
            <v-simple-table dense>
              <thead>
              <tr>
                <th class="dateColumn">
                  Deposit date
                </th>
                <th class="amountColumn">
                  Amount
                </th>
              </tr>
              </thead>
              <tbody>
              <tr v-for="(item, index) in column" :key="index">
                <td>
                  {{ formatDate(item.date) }}
                </td>
                <td class="amountColumn">
                  {{ formatMoney(item.amount) }}
                </td>
              </tr>
              </tbody>
            </v-simple-table>
          </div>
          <div v-else class="noReport">
            No cash receipts reported this period
          </div>
        </div>
      </div>
    </div>
    <v-divider></v-divider>
    <v-row>
      <v-col><h3 class="line-item-title">2. Total cash receipts (line 2 of C4): </h3></v-col>
      <v-col class="mr-4"  style="text-align: right">{{ formatMoney(report.metadata.sums.receipts.cash_receipts.amount) }}</v-col>
    </v-row>
    <v-divider></v-divider>
    <h3 class="line-item-title">
      3. Expenditures (cash and credit transaction detail):
    </h3>
    <v-divider></v-divider>
    <div>
      <v-simple-table dense>
        <thead>
        <tr>
          <th class="dateColumn">
            Date paid
          </th>
          <th>
            Vendor or recipient<br/>
            (Name and address)
          </th>
          <th>
            Purpose of expense<br/>
            and/or description
          </th>
          <th v-if="displayCreditColumn" class="amountColumn">
            Credit/Debt
          </th>
          <th class="amountColumn">
            Cash
          </th>
        </tr>
        </thead>
        <tbody>
        <tr>
          <td>N/A</td>
          <td>Expenses</td>
          <td>Non-itemized expenditures</td>
          <td v-if="displayCreditColumn" class="amountColumn">
            <span v-if="displayNonItemizedCreditAmount" class="amountColumn">{{ formatMoney(report.user_data.sums.non_itemized_credit_expenses.amount) }}</span>
          </td>
          <td class="amountColumn">{{ formatMoney(nonItemizedCashExpenses) }}</td>
        </tr>
        <tr v-for="(item, index) in report.user_data.expenses" :key="index">
          <td>
            {{ formatDate(item.date) }}
          </td>
          <td>
            <ContactInfo :contId="item.contact_key" :contacts="report.user_data.contacts"/>
          </td>
          <td>
            <span v-if="item.category">
              <CategoryInfo :report="report" :categoryId="item.category" v-if="item.category"/>
            </span>
            {{prefixPaidTo(item)}}
            {{ item.description }}
          </td>
          <td v-if="displayCreditColumn" class="amountColumn">
            <span v-if="item.creditor_key">{{formatMoney(item.amount)}}</span>
          </td>
          <td class="amountColumn">
            <span v-if="!item.creditor_key">{{ formatMoney(item.amount) }}</span>
          </td>
        </tr>
        <v-divider></v-divider>
        </tbody>
        <tfoot>
        <tr>
          <td></td>
          <td></td>
          <td></td>
          <td v-if="displayCreditColumn" class="amountColumn font-weight-bold">{{ formatMoney(report.metadata.sums.expenditures.credit_expenditures.amount)}}</td>
          <td class="amountColumn font-weight-bold">{{ formatMoney(report.metadata.sums.expenditures.cash_expenditures.amount)}}</td>
        </tr>
        </tfoot>
      </v-simple-table>
      <v-divider></v-divider>
      <v-row>
        <v-col>
          <h3 class="line-item-title">4. Total expenditures:</h3>
        </v-col>
        <v-col class="mr-4" style="text-align: right">{{ formatMoney(line4Total) }}</v-col>
      </v-row>
    </div>
  </div>
</template>

<script>
import filters from "../filters";
import utils from "./report-utils";
import ContactInfo from "./ContactInfo"
import CategoryInfo from "./CategoryInfo";
import ReportDraftWatermark from "@/components/ReportDraftWatermark.vue";

export default {
  name: "ScheduleA",
  props: {
    report: Object
  },
  components: {ContactInfo, CategoryInfo, ReportDraftWatermark},
  data() {
    return {
      columns: [],
      categoryList: []
    }
  },
  methods: {
    ...filters.filters, ...utils,
    calcDepositColumns(numCols) {
      if (this.report.user_data && this.report.user_data.misc_receipts) {
        let mid = Math.ceil(this.report.user_data.misc_receipts.length / numCols);
        for (let col = 0; col < numCols; col++) {
          this.columns.push(this.report.user_data.misc_receipts.slice(col * mid, col * mid + mid))
        }
        return this.columns
      }
    },
    prefixPaidTo(item) {
      if (item.payee_key && this.report.version >= 1.1) {
        let key = (item.payee_key).toString();
        if (key) {
          let contact_name = this.report.user_data.contacts.filter(c => c.contact_key === key)[0].name;
          return 'Paid to ' + contact_name + '. ';
        }
      }
    },
  },
  computed: {
    nonItemizedCashExpenses () {
      if(this.report.version >= 1.1) {
        return this.report.user_data.sums.non_itemized_cash_expenses.amount;
      }
      else {
        return this.report.user_data.sums.expenses_under_50.amount;
      }
    },
    committee() {
      return this.report.user_data ? this.report.user_data.committee : {};
    },
    displayCreditColumn() {
      return this.report.metadata.sums.expenditures.credit_expenditures?.amount || this.report.version >= 1.1;
    },
    displayNonItemizedCreditAmount() {
      return this.report.user_data.sums.non_itemized_credit_expenses?.amount;
    },
    line4Total(){
      if(this.report.metadata.sums.expenditures.credit_expenditures?.amount){
        return this.report.metadata.sums.expenditures.credit_expenditures.amount + this.report.metadata.sums.expenditures.cash_expenditures.amount;
      }else{
        return this.report.metadata.sums.expenditures.cash_expenditures.amount;
      }
    }
  },
  created() {
    this.calcDepositColumns(3);
    this.sortByDate(this.report.user_data.expenses);
  }
}
</script>

<style scoped>
.schedule { position: relative; }
</style>